import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  Home,
  Building2,
  ScrollText,
  Settings,
  Layers,
  Key,
  Bell,
  FileJson,
  Webhook,
  UserPlus,
  Boxes,
  Eye,
} from "lucide-react";
import { SaasDetails } from "./saas/SaasDetails";
import { ConnectedHotels } from "./saas/ConnectedHotels";
import { SaasPMSLogs } from "./saas/SaasPMSLogs";
import { SaasSettings } from "./saas/SaasSettings";
import { FunctionalScope } from "./saas/FunctionalScope";
import { ApiToken } from "./saas/ApiToken";
import { Webhooks } from "./saas/Webhooks";
import { HotelOnboardingConfig } from "./saas/HotelOnboardingConfig";
import { WidgetOnboardedHotels } from "./saas/WidgetOnboardedHotels";
import { PmsInfo } from "./saas/PmsInfo";
import { UserMenu } from "../UserMenu";
import { ThemeToggle } from "../ThemeToggle";
import { ThemeProvider } from "../../contexts/ThemeContext";
import { NotificationCenter } from "../shared/NotificationCenter";
import { NotificationBell } from "../shared/NotificationBell";
import { useExtranetSection } from "../../hooks/useExtranetSection";
import { ROUTES, SaasSection } from "../../lib/routes";

export function SaasExtranet() {
  const location = useLocation();
  // The section lives in the URL (/extranet/saas/:section), so every page is
  // bookmarkable and the browser's back button works between them.
  const { section: activeSection, sectionPath, goToSection } =
    useExtranetSection("saas");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || "");
      }
    };
    getUser();

    // Check if we should show settings with user tab. The state is carried
    // over so Settings still opens on the "user" tab; the section guard stops
    // the redirect from firing again once we're there.
    if (location.state?.settingsTab === "user" && activeSection !== "settings") {
      goToSection("settings", { replace: true, state: location.state });
    }
  }, [location.state, activeSection, goToSection]);

  const renderContent = () => {
    switch (activeSection) {
      case "details":
        return <SaasDetails />;
      case "hotels":
        return <ConnectedHotels />;
      case "pms_info":
        return <PmsInfo />;
      case "logs":
        return <SaasPMSLogs />;
      case "apiToken":
        return <ApiToken />;
      case "settings":
        return <SaasSettings />;
      case "scope":
        return <FunctionalScope />;
      case "webhooks":
        return <Webhooks />;
      case "onboarding":
        return <HotelOnboardingConfig />;
      case "widgetHotels":
        return <WidgetOnboardedHotels />;
      case "notifications":
        return <NotificationCenter />;
      default:
        return <SaasDetails />;
    }
  };

  const menuItems: { id: SaasSection; label: string; icon: typeof Home }[] = [
    { id: "details", label: "SaaS Details", icon: Home },
    { id: "hotels", label: "Connected Hotels", icon: Building2 },
    { id: "pms_info", label: "PMS Info", icon: Eye },
    { id: "scope", label: "Functional Scope", icon: Layers },
    { id: "webhooks", label: "Webhooks", icon: Webhook },
    { id: "onboarding", label: "Hotel Onboarding", icon: UserPlus },
    { id: "widgetHotels", label: "Hotels configuration", icon: Boxes },
    { id: "logs", label: "PMS Logs", icon: ScrollText },
    { id: "apiToken", label: "API Token", icon: Key },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <ThemeProvider>
      <div className="extranet-layout">
        <div className="extranet-sidebar" style={{ backgroundColor: '#101829' }}>
          {/* Logo */}
          <div className="p-4">
            <div className="flex flex-col">
              <div className="w-40">
                <img
                  src="/images/zeswitch2.png"
                  alt="ZeSwitch"
                  className="w-full h-auto block"
                />
                <img
                  src="/images/zeswitch2.png"
                  alt="ZeSwitch"
                  className="w-full h-auto hidden"
                />
              </div>
              <span className="mt-1 text-sm font-medium text-gray-300">
                for SaaS
              </span>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={sectionPath(item.id)}
                  aria-current={activeSection === item.id ? "page" : undefined}
                  className={`
                    w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                    ${
                      activeSection === item.id
                        ? "bg-[#8B5CF6] text-white"
                        : "text-gray-300 hover:bg-[#1a2332] hover:text-white"
                    }
                  `}
                >
                  <Icon
                    className={`
                    h-5 w-5 mr-3
                    ${
                      activeSection === item.id
                        ? "text-white"
                        : "text-gray-400"
                    }
                  `}
                  />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Menu and Theme Toggle */}
          <div className="p-4 flex items-center justify-between">
            <UserMenu email={userEmail} className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors" onOpenSettings={() => goToSection("settings")} />
            <ThemeToggle />
          </div>
        </div>

        {/* Main Content */}
        <div className="extranet-content">
          {/* Header with notification bell */}
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex justify-end items-center gap-3">
            <Link
              to={ROUTES.apiDocumentation}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#8B5CF6] hover:bg-[#9D72F7] rounded-md transition-colors"
            >
              <FileJson className="h-4 w-4 mr-2" />
              API Documentation
            </Link>
            <NotificationBell />
          </div>
          <div className="extranet-content-inner">{renderContent()}</div>
        </div>
      </div>
    </ThemeProvider>
  );
}
